import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Rate,
  message,
  Row,
  Col,
} from 'antd';
import apiService from '../../../../services/apiService';

const { TextArea } = Input;

interface Vendor {
  id: string;
  name: string;
  code: string;
  email?: string;
  phone?: string;
  address?: string;
  contactPerson?: string;
  paymentTerms?: string;
  status: string;
  rating: number;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

interface VendorFormProps {
  visible: boolean;
  vendor: Vendor | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const VendorForm: React.FC<VendorFormProps> = ({
  visible,
  vendor,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const isEditing = !!vendor;

  useEffect(() => {
    if (visible) {
      if (vendor) {
        form.setFieldsValue({
          name: vendor.name,
          code: vendor.code,
          email: vendor.email,
          phone: vendor.phone,
          address: vendor.address,
          contactPerson: vendor.contactPerson,
          paymentTerms: vendor.paymentTerms,
          status: vendor.status,
          rating: vendor.rating,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          status: 'ACTIVE',
          rating: 0,
        });
      }
    }
  }, [visible, vendor, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (isEditing) {
        await apiService.put(`/accounting/vendors/${vendor.id}`, values);
        message.success('Vendor updated successfully');
      } else {
        await apiService.post('/accounting/vendors', values);
        message.success('Vendor created successfully');
      }

      onSuccess();
    } catch (error: any) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      console.error('Failed to save vendor:', error);
      message.error(error.message || 'Failed to save vendor');
    }
  };

  return (
    <Modal
      title={isEditing ? 'Edit Vendor' : 'Add New Vendor'}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={isEditing ? 'Update' : 'Create'}
      width={700}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 'ACTIVE',
          rating: 0,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Vendor Name"
              rules={[{ required: true, message: 'Please enter vendor name' }]}
            >
              <Input placeholder="Enter vendor name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="code"
              label="Vendor Code"
              rules={[{ required: true, message: 'Please enter vendor code' }]}
            >
              <Input placeholder="e.g., VND001" disabled={isEditing} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: 'email', message: 'Please enter a valid email' }]}
            >
              <Input placeholder="vendor@example.com" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="phone" label="Phone">
              <Input placeholder="Phone number" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="contactPerson" label="Contact Person">
              <Input placeholder="Contact person name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="paymentTerms" label="Payment Terms">
              <Select placeholder="Select payment terms" allowClear>
                <Select.Option value="Net 7">Net 7</Select.Option>
                <Select.Option value="Net 15">Net 15</Select.Option>
                <Select.Option value="Net 30">Net 30</Select.Option>
                <Select.Option value="Net 45">Net 45</Select.Option>
                <Select.Option value="Net 60">Net 60</Select.Option>
                <Select.Option value="Net 90">Net 90</Select.Option>
                <Select.Option value="Due on Receipt">Due on Receipt</Select.Option>
                <Select.Option value="COD">COD</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="address" label="Address">
          <TextArea rows={2} placeholder="Full address" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select>
                <Select.Option value="ACTIVE">Active</Select.Option>
                <Select.Option value="INACTIVE">Inactive</Select.Option>
                <Select.Option value="BLACKLISTED">Blacklisted</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="rating" label="Rating">
              <Rate allowHalf />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default VendorForm;
